import React, { useEffect, useState } from "react"
import { useNavigate } from "@tanstack/react-router"
import { z } from "zod"

import type { ContactsGet } from "@productlane/api"
import { normalizeEmail } from "@productlane/api/src/lib/contact"
import { generateNameFromEmail } from "@productlane/api/src/lib/name-from-email"
import { Button, Dialog, Form, FormInput, useZodForm } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"
import { queries } from "@productlane/zero/queries"

import { toast } from "@/components/toast"
import {
	EMAIL_MAXIMUM_LENGTH,
	EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE,
} from "@/constants"
import { analytics } from "@/lib/analytics"
import { generateId } from "@/lib/id"
import { useSession } from "@/lib/session"
import { mutateOk, useZero } from "@/lib/zero"

export type PartialContactCreate = Pick<
	ContactsGet[number],
	"id" | "name" | "email" | "companyId"
>

interface CreateContactDialogProps {
	isOpen: boolean
	onOpenChange: (
		value: boolean,
		created: PartialContactCreate | undefined,
	) => void
	defaultValue?: string
}

const CreateContactFormSchema = z.object({
	name: z.string().min(1),
	email: z
		.string()
		.email()
		.max(EMAIL_MAXIMUM_LENGTH, EMAIL_MAXIMUM_LENGTH_ERROR_MESSAGE),
})

function getDefaultName(defaultValue?: string) {
	if (!defaultValue) {
		return undefined
	}
	if (!defaultValue.includes("@")) {
		return defaultValue
	}
	return generateNameFromEmail(defaultValue)
}

function getDefaultEmail(defaultValue?: string) {
	if (!defaultValue) {
		return undefined
	}
	if (!defaultValue.includes("@")) {
		return undefined
	}
	return defaultValue
}

export const CreateContactDialog = React.memo(
	({ isOpen, onOpenChange, defaultValue }: CreateContactDialogProps) => {
		const [segmentsSelected, setSegementsSelected] = useState<string[]>([])
		const zero = useZero()
		const { session } = useSession()

		const navigate = useNavigate()
		const form = useZodForm({
			schema: CreateContactFormSchema,
			defaultValues: {
				name: getDefaultName(defaultValue),
				email: getDefaultEmail(defaultValue),
			},
		})
		useEffect(() => {
			if (isOpen) {
				// init because tagGroups are not defined on first render
				form.reset({
					name: getDefaultName(defaultValue),
					email: getDefaultEmail(defaultValue),
				})
			} else {
				form.reset()
				setSegementsSelected([])
			}
			// eslint-disable-next-line react-hooks/exhaustive-deps
		}, [form, isOpen])

		return (
			<Dialog
				isOpen={isOpen}
				onOpenChange={(value) => {
					onOpenChange(value, undefined)
				}}
				title="Create contact"
				footer={
					<Button form="create-contact-form" type="submit">
						Submit
					</Button>
				}
				onCloseAutoFocus={(event) => {
					//? https://github.com/radix-ui/primitives/issues/1241
					event.preventDefault()
					document.body.style.pointerEvents = ""
				}}
				onInteractOutside={(event) => event.preventDefault()}
			>
				<Form
					id="create-contact-form"
					form={form}
					className="space-y-4"
					onSubmit={async (values) => {
						if (!session?.workspaceId) {
							return
						}
						const normalizedEmail = normalizeEmail(values.email)

						const payload = {
							id: generateId(),
							email: normalizedEmail,
							name: values.name,
							companyId: undefined,
						}
						const existingContact = await zero.run(
							queries.contactByEmail(normalizedEmail),
							{ type: "complete" },
						)
						if (existingContact) {
							toast.error({
								title: "Failed to create contact",
								description: "This email is already used by another contact",
							})
							return
						}
						await mutateOk(
							zero.mutate(
								mutators.contacts.create({
									...payload,
									segmentIds: segmentsSelected,
								}),
							),
						)
						onOpenChange(false, { ...payload, companyId: null })
						form.reset()
						toast.success({
							title: "Contact created",
							descriptionAction: {
								label: `${values.name}`,
								callback: () =>
									void navigate({
										to: "/people/$id",
										params: { id: payload.id },
									}),
							},
							description: "created",
						})
						analytics.track("create-contact")
						void navigate({ to: "/people/$id", params: { id: payload.id } })
					}}
				>
					<FormInput name="name" label="Name" />
					<FormInput name="email" label="Email" />
				</Form>
			</Dialog>
		)
	},
)
